import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Profile from '../comp/Profile';
import ThemeSwitcher from '../comp/ThemeSwitcher';

const Icons = {
  Plus: <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>,
  Copy: <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>,
  Share: <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="18" cy="5" r="3"></circle><circle cx="6" cy="12" r="3"></circle><circle cx="18" cy="19" r="3"></circle><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line></svg>,
  Doodle: <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>,
  Ghost: <svg className="w-16 h-16 text-slate-300 dark:text-gray-700" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 2a8 8 0 018 8v10a2 2 0 01-2 2h-2a2 2 0 01-2-2 2 2 0 00-2-2 2 2 0 00-2 2 2 2 0 01-2 2H6a2 2 0 01-2-2V10a8 8 0 018-8z M8 10a1 1 0 011-1h.01a1 1 0 010 2H9a1 1 0 01-1-1z M14 10a1 1 0 011-1h.01a1 1 0 010 2H15a1 1 0 01-1-1z" /></svg>,
  Lock: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>,
  Unlock: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" /></svg>,

};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const cardVariants = {
  hidden: { opacity: 0, y: 20, rotate: 0 },
  visible: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 120 } }
};

const FloatingShapes = () => (
  <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
    <motion.svg animate={{ y: [0, -15, 0], rotate: [0, 5, 0] }} transition={{ duration: 7, repeat: Infinity }} className="absolute top-32 left-[5%] w-24 h-24 text-indigo-200/50 dark:text-indigo-900/30" viewBox="0 0 100 100"><circle cx="50" cy="50" r="40" stroke="currentColor" strokeWidth="3" fill="none" /></motion.svg>
    <motion.svg animate={{ y: [0, 20, 0], x: [0, 10, 0] }} transition={{ duration: 9, repeat: Infinity }} className="absolute bottom-20 right-[10%] w-32 h-32 text-pink-200/50 dark:text-pink-900/30" viewBox="0 0 100 100"><rect x="10" y="10" width="80" height="80" stroke="currentColor" strokeWidth="3" fill="none" /></motion.svg>
  </div>
);

export default function BoardList({ data }) {
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [newBoardName, setNewBoardName] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [copySuccess, setCopySuccess] = useState(null);

  const fetchBoards = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_APP_URL}/boards`, { credentials: 'include' });
      const result = await res.json();
      if (result.req) setBoards(result.boards);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBoards(); }, []);

  const handleCreate = async () => {
    if (!newBoardName.trim()) return;
    const res = await fetch(`${process.env.REACT_APP_APP_URL}/createboard`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ boardname: newBoardName, isPrivate: isPrivate }),
    });
    const result = await res.json();
    if (result.req) {
      setModalOpen(false);
      setNewBoardName('');
      setIsPrivate(false);
      window.location.href = `${process.env.REACT_APP_URL}/b/${result.board}`;
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const copyToClipboard = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopySuccess(link);
      setTimeout(() => setCopySuccess(null), 2000);
    } catch (err) { console.error(err); }
  };

  const shareBoard = async (link, title) => {
    if (navigator.share) {
      try { await navigator.share({ title: `Join my Sketchboard: ${title}`, url: link }); }
      catch (err) { console.error(err); }
    } else {
      alert('Web Share API not supported on this browser.');
    }
  };

  return (
    <div className="min-h-screen w-full flex flex-col bg-[#FCF8F8] dark:bg-[#1a1111] relative font-sans selection:bg-[#F5AFAF] selection:text-[#452829]">

      {/* --- Global Styles --- */}
      <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap');
      .font-body { font-family: 'Outfit', sans-serif; }
      
      /* Subtle Grid Background pattern */
      .bg-grid-pattern {
        background-size: 40px 40px;
        background-image: linear-gradient(to right, rgba(69, 40, 41, 0.03) 1px, transparent 1px),
                          linear-gradient(to bottom, rgba(69, 40, 41, 0.03) 1px, transparent 1px);
      }
      .dark .bg-grid-pattern {
        background-image: linear-gradient(to right, rgba(249, 223, 223, 0.03) 1px, transparent 1px),
                          linear-gradient(to bottom, rgba(249, 223, 223, 0.03) 1px, transparent 1px);
      }
    `}</style>

      <div className="fixed inset-0 bg-grid-pattern pointer-events-none z-0"></div>
      {/* Floating shapes component assumed here */}
      <FloatingShapes />
      <div className="relative z-10 w-full px-6 py-6 border-b border-[#452829]/5 dark:border-[#FBEFEF]/5 bg-[#FCF8F8]/80 dark:bg-[#1a1111]/80 backdrop-blur-md sticky top-0">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group sm:px-5 sm:py-2 p-1 transition-transform hover:scale-105">
            <span className='bg-[#452829] dark:bg-[#F5AFAF] text-[#FCF8F8] dark:text-[#452829] p-1.5 rounded-lg group-hover:rotate-12 transition-transform duration-300'>
              {Icons.Doodle}
            </span>
            <span className="font-body font-bold text-lg tracking-tight hidden sm:block text-[#452829] dark:text-[#FBEFEF]">
              doodle<span className='text-[#F5AFAF]'>up</span>
            </span>
          </Link>
          <div className='flex items-center gap-4'>
            <Profile data={data} />
          </div>
        </div>
      </div>

      {/* --- Main Content --- */}
      <div className="relative z-10 mx-auto max-w-7xl px-6 py-12 w-full flex-1">

        {/* Page Header */}
        <div className="mb-12 flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h1 className="font-body font-extrabold text-4xl text-[#452829] dark:text-[#F9DFDF] mb-2">
              My Gallery
            </h1>
            <p className="text-[#452829]/60 dark:text-[#FBEFEF]/60 text-lg">
              Manage your sketches and ideas.
            </p>
          </div>
          <div className="bg-white dark:bg-[#2d1f1f] px-4 py-2 rounded-full border border-[#452829]/10 dark:border-[#F5AFAF]/10 text-sm font-bold text-[#452829] dark:text-[#FBEFEF] shadow-sm">
            {boards.length} Canvas{boards.length !== 1 && 'es'}
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-64 bg-[#452829]/5 dark:bg-[#FBEFEF]/5 rounded-3xl animate-pulse"></div>
            ))}
          </div>
        ) : (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 pb-12"
          >

            {/* Create New Card */}
            <motion.div
              variants={cardVariants}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setModalOpen(true)}
              className="cursor-pointer group h-64 rounded-[2rem] border-2 border-dashed border-[#452829]/10 dark:border-[#FBEFEF]/10 hover:border-[#F5AFAF] dark:hover:border-[#F5AFAF] bg-transparent hover:bg-[#F5AFAF]/5 dark:hover:bg-[#F5AFAF]/5 transition-all duration-300 flex flex-col items-center justify-center gap-4"
            >
              <div className="w-16 h-16 rounded-full bg-[#452829]/5 dark:bg-[#FBEFEF]/5 group-hover:bg-[#F5AFAF] group-hover:text-[#452829] text-[#452829] dark:text-[#FBEFEF] flex items-center justify-center transition-colors duration-300 shadow-sm">
                {Icons.Plus}
              </div>
              <span className="font-bold text-lg text-[#452829]/60 dark:text-[#FBEFEF]/60 group-hover:text-[#452829] dark:group-hover:text-[#FBEFEF] transition-colors">
                New Canvas
              </span>
            </motion.div>

            {/* Board Cards */}
            {boards.length > 0 ? (
              boards.map((board) => {
                const link = `${process.env.REACT_APP_URL}/b/${board.id}`;

                return (
                  <motion.div
                    variants={cardVariants}
                    key={board.id}
                    className="group relative h-64 flex flex-col justify-between bg-white dark:bg-[#2d1f1f] rounded-[2rem] p-6 border border-[#452829]/5 dark:border-[#F5AFAF]/10 shadow-xl shadow-[#452829]/5 dark:shadow-black/20 hover:shadow-2xl hover:shadow-[#452829]/10 hover:-translate-y-1 transition-all duration-300 overflow-hidden"
                  >
                    {/* Top Bar */}
                    <div className="flex justify-between items-start mb-4">
                      <span className="bg-[#FBEFEF] dark:bg-[#452829] text-[#452829] dark:text-[#FBEFEF] text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                        Sketch
                      </span>
                      <span className="text-[10px] font-bold text-[#452829]/40 dark:text-[#FBEFEF]/40 uppercase tracking-wider">
                        {formatDate(board.createdAt)}
                      </span>
                    </div>

                    {/* Title */}
                    <div className="mb-auto">
                      <h3 className="font-bold text-xl text-[#452829] dark:text-[#F9DFDF] leading-snug line-clamp-2">
                        {board.boardname}
                      </h3>
                    </div>

                    {/* Action Area (Reveals on Hover for Desktop) */}
                    <div className="pt-4 border-t border-[#452829]/5 dark:border-[#FBEFEF]/5 mt-4">
                      <a href={link} className="block w-full py-2.5 bg-[#452829] dark:bg-[#FBEFEF] text-[#FBEFEF] dark:text-[#452829] text-center rounded-xl font-bold text-sm mb-3 sm:translate-y-0 sm:group-hover:-translate-y-2 transition-all duration-300">
                        Open Canvas
                      </a>

                      <div className="flex justify-between items-center px-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); copyToClipboard(link); }}
                          className="text-xs font-bold text-[#452829]/50 dark:text-[#FBEFEF]/50 hover:text-[#452829] dark:hover:text-[#FBEFEF] transition-colors flex items-center gap-1.5"
                        >
                          {copySuccess === link ? <span className="text-green-500">Copied!</span> : <>{Icons.Copy} Copy</>}
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); shareBoard(link, board.boardname); }}
                          className="text-xs font-bold text-[#452829]/50 dark:text-[#FBEFEF]/50 hover:text-[#452829] dark:hover:text-[#FBEFEF] transition-colors flex items-center gap-1.5"
                        >
                          {Icons.Share} Share
                        </button>
                      </div>
                    </div>
                  </motion.div>
                );
              })
            ) : (
              <motion.div variants={cardVariants} className="col-span-1 sm:col-span-2 md:col-span-3 flex flex-col items-center justify-center py-20 opacity-50">
                <div className="text-6xl mb-4 grayscale opacity-30">👻</div>
                <p className="font-bold text-xl text-[#452829] dark:text-[#FBEFEF]">It's quiet here...</p>
                <p className="text-sm text-[#452829]/60 dark:text-[#FBEFEF]/60">Create your first canvas above.</p>
              </motion.div>
            )}
          </motion.div>
        )}
      </div>

      {/* --- Create Modal --- */}
      <AnimatePresence>
        {modalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[#452829]/20 dark:bg-black/60 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-[#FCF8F8] dark:bg-[#1a1111] w-full max-w-md rounded-[2.5rem] p-8 shadow-2xl border border-[#452829]/10 dark:border-[#F5AFAF]/10 relative overflow-hidden"
            >
              {/* Background Decor */}
              <div className="absolute -top-20 -right-20 w-64 h-64 bg-[#F5AFAF]/10 rounded-full blur-3xl pointer-events-none"></div>

              <button
                onClick={() => setModalOpen(false)}
                className="absolute z-100 top-6 right-6 w-8 h-8 flex items-center justify-center rounded-full bg-[#FBEFEF] dark:bg-[#2d1f1f] text-[#452829] dark:text-[#FBEFEF] hover:scale-110 transition-transform"
              >
                ✕
              </button>

              <div className="mb-8 relative max-w-[200px]">
                <h2 className="font-body font-bold text-3xl text-[#452829] dark:text-[#F9DFDF] mb-2">Create New</h2>
                <p className="text-[#452829]/60 dark:text-[#FBEFEF]/60 text-sm">Give your new idea a name.</p>
              </div>

              <div className="space-y-6 relative z-10">
                <div>
                  <label className="block text-xs font-extrabold text-[#452829]/40 dark:text-[#FBEFEF]/40 uppercase tracking-widest mb-3 ml-1">Name</label>
                  <input
                    type="text"
                    autoFocus
                    placeholder="e.g. Brainstorming Session"
                    className="w-full px-5 py-4 text-lg font-bold bg-white dark:bg-[#2d1f1f] border border-[#452829]/10 dark:border-[#FBEFEF]/10 rounded-2xl focus:outline-none focus:border-[#F5AFAF] focus:ring-4 focus:ring-[#F5AFAF]/10 text-[#452829] dark:text-[#FBEFEF] placeholder:text-[#452829]/20 dark:placeholder:text-[#FBEFEF]/20 transition-all"
                    value={newBoardName}
                    onChange={(e) => setNewBoardName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                  />
                </div>

                {/* Privacy Toggle */}
                <div className="flex items-center justify-between p-4 bg-white dark:bg-[#2d1f1f] rounded-2xl border border-[#452829]/10 dark:border-[#FBEFEF]/10">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg ${isPrivate ? 'bg-[#F5AFAF] text-[#452829]' : 'bg-[#FBEFEF] text-[#452829]/40'}`}>
                      {isPrivate ? Icons.Lock : Icons.Unlock}
                    </div>
                    <div>
                      <div className="font-bold text-[#452829] dark:text-[#F9DFDF] text-sm">
                        {isPrivate ? 'Private' : 'Public'}
                      </div>
                      <div className="text-xs text-[#452829]/50 dark:text-[#FBEFEF]/50">
                        {isPrivate ? 'Invite only' : 'Open via link'}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setIsPrivate(!isPrivate)}
                    className={`w-12 h-7 rounded-full transition-colors relative ${isPrivate ? 'bg-[#452829] dark:bg-[#F5AFAF]' : 'bg-[#452829]/10 dark:bg-[#FBEFEF]/10'}`}
                  >
                    <div className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-white shadow-sm transform transition-transform ${isPrivate ? 'translate-x-5' : 'translate-x-0'}`}></div>
                  </button>
                </div>

                <button
                  onClick={handleCreate}
                  disabled={!newBoardName.trim()}
                  className="w-full bg-[#452829] dark:bg-[#FBEFEF] text-[#FBEFEF] dark:text-[#452829] py-4 rounded-2xl font-bold text-lg hover:bg-[#2a1818] dark:hover:bg-white shadow-xl shadow-[#452829]/20 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  Create Canvas <span>→</span>
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>



    </div>
  );
}